import { Injectable } from '@angular/core';
import { filter } from 'rxjs';
import { ListComponent } from './list.component';
import { Alia, Column } from './interfaces';
import { MenuItem } from 'primeng/api';
import { InputSwitchChangeEvent } from 'primeng/inputswitch';
import { BehaviorSubject } from 'rxjs';
import { TableEditCompleteEvent } from 'primeng/table';

@Injectable({
  providedIn: 'root',
})
export class ListPresenter<T> {
  view!: ListComponent<T>;
  isEnableActionButtonsSubject = new BehaviorSubject<boolean>(false);
  isEnableActionButtons$ = this.isEnableActionButtonsSubject.asObservable();

  setView(view: ListComponent<T>): void {
    this.view = view;

    this.view.data$
      .pipe(filter((data): data is T[] => !!data))
      .subscribe((value) => {
        const clonedData = JSON.parse(JSON.stringify(value)) as T[];

        if(this.view.alias.length>0) this.view.data = this.mapLabelsByAlias(clonedData, this.view.alias);
        else this.view.data = clonedData;

      });
  }

  isHighlightColumn(_index: number): MenuItem | null {
    if (!this.view.highlightlastColumn) return null;

    return this.view.columns?.[_index]?.highlightBorder ? {'text-align':'right'} : null;
  }

  mapLabelsByAlias<T>(items: T[], mappings: Alia[]): T[] {
    const result: T[] = [];
    const remainingItems: T[] = [];

    // First, process items in the order of mappings
    mappings.forEach(mapping => {
      const item = items.find(item => (item as any).label === mapping.field);
      if (item) {
        result.push({
          ...item,
          label: mapping.alia
        });
      }
    });

    // Then add items that don't have mappings
    items.forEach(item => {
      if (!mappings.some(m => m.field === (item as any).label)) {
        remainingItems.push(item);
      }
    });

    return [...result, ...remainingItems];
  }

  isSpecialField(columnn: Column, index: number): MenuItem {
    const isSpecialField = !!this.view.specialsFields.includes(
      (columnn as MenuItem)['label'] as string
    );
    const isHighlightColumn = !!this.isHighlightColumn(index);

    return {
      color: isSpecialField ? 'var(--primary-color)' : null,
      'border-top':
        isHighlightColumn && isSpecialField && this.view.highlightlastColumn
          ? '3px solid var(--primary-color)'
          : null,
      'padding-top':
        isHighlightColumn && isSpecialField && this.view.highlightlastColumn
          ? '2px'
          : null,
      'text-align':
        isHighlightColumn && this.view.highlightlastColumn ? 'right' : null,
    };
  }

  getDynamicValue<T extends Record<string, T>>(
    obj: T,
    path: string
  ): T | undefined {
    if (!obj || !path) return undefined;

    const pathParts = path.split('.');

    return pathParts.reduce((acc, key) => {
      if (Array.isArray(acc)) return acc[parseInt(key, 10)];

      if (acc && acc[key] !== undefined) return acc[key];
    }, obj);
  }

  setDynamicValue<T extends Record<string, unknown>>(
    row: T,
    path: string,
    value: unknown
  ): void {
    const keys = path.split('.');
    let result: Record<string, unknown> = row;

    // Iterate over the keys to access the nested property
    for (let i = 0; i < keys.length - 1; i++) {
      // Access the property dynamically and check if it's valid
      if (result[keys[i]] && typeof result[keys[i]] === 'object') {
        result = result[keys[i]] as Record<string, unknown>; // Update `result` as a nested object
      } else {
        return; // If any key doesn't exist or isn't an object, exit
      }
    }

    // Finally, set the value of the last property in the path
    result[keys[keys.length - 1]] = value;
  }

  isNumber(row: T): boolean {
    return typeof row === 'number'
  }

  onRowEdit(event: TableEditCompleteEvent): void {
    this.view.actionDataEditTriggered.emit(this.view.data as T[]);
    this.view.actionRowEditTriggered.emit(event);
  }

  toggleChange({ checked }: InputSwitchChangeEvent, row: T): void {
    this.view.actionToggleTriggered.emit({ checked, data: row });
  }

  hasMultipleDynamicValues<T extends Record<string, T>>(
    obj: T,
    path: string,
    parentPath: string,
    checkAll: boolean = true,
    expectedValue?: unknown
  ): boolean {
    const dynamicArray = this.getDynamicValue(obj, path);

    // Si no es un array o está vacío, retorna false
    if (!Array.isArray(dynamicArray) || dynamicArray.length === 0) return false;

    // Si no hay parentPath, verificamos los valores directos del array
    if (!parentPath) {
      return checkAll
        ? dynamicArray.every(
            (item) =>
              Boolean(item) ===
              (expectedValue !== undefined ? expectedValue : true)
          )
        : dynamicArray.some(
            (item) =>
              Boolean(item) ===
              (expectedValue !== undefined ? expectedValue : true)
          );
    }

    // Si hay parentPath, verificamos la propiedad en cada elemento
    return checkAll
      ? dynamicArray.every((item) => {
          const value = this.getDynamicValue(item, parentPath);
          return expectedValue !== undefined
            ? value === expectedValue
            : Boolean(value);
        })
      : dynamicArray.some((item) => {
          const value = this.getDynamicValue(item, parentPath);
          return expectedValue !== undefined
            ? value === expectedValue
            : Boolean(value);
        });
  }

  onCellEditorKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.stopPropagation();
    }
  }
}
